/**
 * Reusable date chip + popover for Treatment Record surfaces (PRD.182 §7.5).
 *
 * Used by the Quick Toolbar and the Charting / Invoice drawers so users can
 * change the target date inline. Popover surfaces a Today shortcut, recent
 * Visit Note dates, and an "Other date" item that opens the native date
 * picker via `showPicker()` (with focus/click fallback for older browsers).
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { NavIcon } from '../../../shell/shared';
import { usePowerSyncQuery } from '../../../adapters/powersync';
import { qEncountersByPatient, type PatientEncounterRow } from '@helios/data-access';

export function todayLocalISO(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function formatDateChip(iso: string): string {
  const today = todayLocalISO();
  if (iso === today) return 'Today';
  try {
    return new Date(iso + 'T00:00:00').toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

interface Props {
  patientId: string;
  date: string;
  onChange: (iso: string) => void;
  /** Render the popover above the chip instead of below. */
  align?: 'below' | 'above';
}

export function DateChipPopover({ patientId, date, onChange, align = 'below' }: Props) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);

  const { data: encounterRows = [] } = usePowerSyncQuery<PatientEncounterRow>(
    qEncountersByPatient(patientId),
  );
  const recentVisitDates = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const e of encounterRows) {
      const iso = (e.started_at ?? e.created_at ?? '').slice(0, 10);
      if (!iso || seen.has(iso)) continue;
      seen.add(iso);
      out.push(iso);
      if (out.length >= 6) break;
    }
    return out;
  }, [encounterRows]);

  useEffect(() => {
    if (!open) return;
    const handle = (e: MouseEvent) => {
      const target = e.target as Node;
      if (wrapRef.current?.contains(target)) return;
      if (popoverRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  // Position the portal-rendered popover anchored to the chip. Recompute
  // on open and on scroll/resize so it stays attached.
  useLayoutEffect(() => {
    if (!open) return;
    const compute = () => {
      const chip = wrapRef.current?.getBoundingClientRect();
      if (!chip) return;
      const popoverHeight = popoverRef.current?.offsetHeight ?? 240;
      const popoverWidth = popoverRef.current?.offsetWidth ?? 220;
      const gap = 4;
      const top = align === 'above' ? chip.top - popoverHeight - gap : chip.bottom + gap;
      // Right-align with the chip; clamp to viewport so it doesn't overflow.
      let left = chip.right - popoverWidth;
      const minLeft = 8;
      const maxLeft = window.innerWidth - popoverWidth - 8;
      left = Math.max(minLeft, Math.min(left, maxLeft));
      setCoords({ top, left });
    };
    compute();
    window.addEventListener('scroll', compute, true);
    window.addEventListener('resize', compute);
    return () => {
      window.removeEventListener('scroll', compute, true);
      window.removeEventListener('resize', compute);
    };
  }, [open, align]);

  const isToday = date === todayLocalISO();

  return (
    <div ref={wrapRef} style={{ position: 'relative', flexShrink: 0 }}>
      <button
        type="button"
        title="Target date for entries"
        onClick={() => setOpen((v) => !v)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          height: 36,
          padding: '0 12px',
          borderRadius: 'var(--radius-full)',
          border: `1px solid ${isToday ? 'var(--color-primary)' : 'var(--border)'}`,
          background: isToday ? 'var(--color-primary-subtle, #fffbeb)' : 'var(--bg-surface)',
          color: isToday ? 'var(--color-primary)' : 'var(--text)',
          fontSize: 'var(--font-size-sm)',
          fontWeight: 'var(--font-weight-medium)',
          cursor: 'pointer',
        }}
      >
        <NavIcon name="calendar" />
        {formatDateChip(date)}
        <NavIcon name="chevron-down" />
      </button>

      <input
        ref={inputRef}
        type="date"
        value={date}
        onChange={(e) => {
          onChange(e.target.value || todayLocalISO());
          setOpen(false);
        }}
        style={{
          position: 'absolute',
          left: 0,
          bottom: 0,
          width: 1,
          height: 1,
          opacity: 0,
          pointerEvents: 'none',
        }}
      />

      {open &&
        createPortal(
          <div
            ref={popoverRef}
            style={{
              position: 'fixed',
              top: coords?.top ?? -9999,
              left: coords?.left ?? -9999,
              zIndex: 1000,
              minWidth: 220,
              padding: 6,
              background: 'var(--bg-surface)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-md)',
              boxShadow: '0 6px 24px rgba(0,0,0,0.12)',
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              visibility: coords ? 'visible' : 'hidden',
            }}
          >
            <DatePopoverItem
              label="Today"
              hint={formatDateChip(todayLocalISO())}
              active={isToday}
              onSelect={() => {
                onChange(todayLocalISO());
                setOpen(false);
              }}
            />
            {recentVisitDates.length > 0 && (
              <>
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.4px',
                    color: 'var(--text-placeholder)',
                    padding: '6px 8px 2px',
                  }}
                >
                  Recent visits
                </div>
                {recentVisitDates.map((iso) => (
                  <DatePopoverItem
                    key={iso}
                    label={formatDateChip(iso)}
                    hint={iso}
                    active={iso === date}
                    onSelect={() => {
                      onChange(iso);
                      setOpen(false);
                    }}
                  />
                ))}
              </>
            )}
            <div style={{ height: 1, background: 'var(--border)', margin: '4px 0' }} />
            <DatePopoverItem
              label="Pick another date…"
              onSelect={() => {
                setOpen(false);
                const input = inputRef.current;
                if (input) {
                  if (typeof input.showPicker === 'function') {
                    try {
                      input.showPicker();
                    } catch {
                      input.focus();
                      input.click();
                    }
                  } else {
                    input.focus();
                    input.click();
                  }
                }
              }}
            />
          </div>,
          document.body,
        )}
    </div>
  );
}

function DatePopoverItem({
  label,
  hint,
  active,
  onSelect,
}: {
  label: string;
  hint?: string;
  active?: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        width: '100%',
        padding: '8px 10px',
        borderRadius: 'var(--radius-sm)',
        border: 'none',
        background: active ? 'var(--color-primary-subtle, #fffbeb)' : 'transparent',
        color: active ? 'var(--color-primary)' : 'var(--text)',
        fontSize: 'var(--font-size-sm)',
        fontWeight: active ? 600 : 500,
        textAlign: 'left',
        cursor: 'pointer',
      }}
      onMouseEnter={(e) => {
        if (!active) (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-hover)';
      }}
      onMouseLeave={(e) => {
        if (!active) (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
      }}
    >
      <span>{label}</span>
      {hint && hint !== label && (
        <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 500 }}>{hint}</span>
      )}
    </button>
  );
}
